import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';

import { ApiService } from '../../core/services/api.service';

interface AccountRow {
  id: string;
  name: string;
  code?: string;
  description?: string;
  isActive: boolean;
  projectCount?: number;
  createdAt?: string;
}

@Component({
  selector: 'app-accounts',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule
  ],
  template: `
<div class="page">
  <header>
    <div>
      <h1>Accounts</h1>
      <p>Organize projects under accounts (Appnest, Solution Mart, Appverse, Unpublished App, …).</p>
    </div>
  </header>

  @if (error()) {
    <div class="error">{{ error() }}</div>
  }

  <div class="layout">
    <mat-card class="form-card">
      <h3>{{ editingId() ? 'Edit account' : 'Create account' }}</h3>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Account name</mat-label>
        <input matInput [(ngModel)]="formName" />
      </mat-form-field>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Code</mat-label>
        <input matInput [(ngModel)]="formCode" />
      </mat-form-field>
      <mat-form-field appearance="outline" class="full">
        <mat-label>Description</mat-label>
        <textarea matInput rows="3" [(ngModel)]="formDescription"></textarea>
      </mat-form-field>
      <div class="actions">
        <button mat-flat-button color="primary" type="button" (click)="save()">
          {{ editingId() ? 'Update' : 'Create' }}
        </button>
        @if (editingId()) {
          <button mat-stroked-button type="button" (click)="resetForm()">Cancel</button>
        }
      </div>
    </mat-card>

    <div class="list">
      @if (loading()) {
        <mat-spinner diameter="36"></mat-spinner>
      }
      @for (a of accounts(); track a.id) {
        <mat-card class="account-card">
          <div class="head">
            <div>
              <h3>{{ a.name }}</h3>
              <p>{{ a.description || a.code || '—' }}</p>
            </div>
            <span class="status" [class.off]="!a.isActive">{{ a.isActive ? 'Active' : 'Inactive' }}</span>
          </div>
          <p class="meta">{{ a.projectCount ?? 0 }} projects · created {{ a.createdAt | date: 'mediumDate' }}</p>
          <div class="actions">
            <a mat-stroked-button [routerLink]="['/projects']" [queryParams]="{ accountId: a.id }">View projects</a>
            <a mat-stroked-button [routerLink]="['/pm-advanced']" [queryParams]="{ tab: 'portfolio', accountId: a.id }">Portfolio roadmap</a>
            <button mat-button type="button" (click)="edit(a)">Edit</button>
            <button mat-button type="button" (click)="toggleActive(a)">
              {{ a.isActive ? 'Deactivate' : 'Activate' }}
            </button>
          </div>
        </mat-card>
      } @empty {
        @if (!loading()) {
          <mat-card><p>No accounts yet.</p></mat-card>
        }
      }
    </div>
  </div>
</div>
  `,
  styles: [
    `
      .page { max-width: 1100px; }
      header { margin-bottom: 1.25rem; }
      h1 { margin: 0 0 0.4rem; font-size: clamp(1.55rem, 1.3rem + 0.8vw, 1.9rem); font-weight: 700; letter-spacing: -0.03em; }
      header p { margin: 0; color: var(--tt-muted); max-width: 40rem; line-height: 1.5; }
      .error { margin-bottom: 1rem; }
      .layout { display: grid; grid-template-columns: 320px 1fr; gap: 1.15rem; align-items: start; }
      .form-card, .account-card { padding: 1.05rem 1.1rem; }
      .form-card h3 { margin: 0 0 0.75rem; font-size: 1rem; font-weight: 700; }
      .full { width: 100%; }
      .actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
      .list { display: grid; gap: 0.75rem; }
      .head { display: flex; justify-content: space-between; gap: 0.75rem; align-items: flex-start; }
      .head h3 { margin: 0; font-size: 1.05rem; letter-spacing: -0.02em; }
      .head p { margin: 0.3rem 0 0; color: var(--tt-muted); }
      .status { background: var(--tt-success-soft); color: var(--tt-success); border-radius: 999px; padding: 0.2rem 0.65rem; font-size: 0.72rem; font-weight: 600; }
      .status.off { background: var(--tt-bg); color: var(--tt-muted); }
      .meta { color: var(--tt-muted); font-size: 0.85rem; }
      @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
    `
  ]
})
export class AccountsComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly accounts = signal<AccountRow[]>([]);
  readonly editingId = signal<string | null>(null);

  formName = '';
  formCode = '';
  formDescription = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getPaged<AccountRow>('v1/accounts', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.accounts.set(page.items ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load accounts.');
      }
    });
  }

  edit(a: AccountRow): void {
    this.editingId.set(a.id);
    this.formName = a.name;
    this.formCode = a.code || '';
    this.formDescription = a.description || '';
  }

  resetForm(): void {
    this.editingId.set(null);
    this.formName = '';
    this.formCode = '';
    this.formDescription = '';
  }

  save(): void {
    if (!this.formName.trim()) {
      this.error.set('Account name is required.');
      return;
    }
    const body = {
      name: this.formName.trim(),
      code: this.formCode.trim() || null,
      description: this.formDescription || null,
      isActive: true
    };
    const id = this.editingId();
    const req = id
      ? this.api.put(`v1/accounts/${id}`, body)
      : this.api.post('v1/accounts', body);

    req.subscribe({
      next: () => {
        this.resetForm();
        this.error.set(null);
        this.load();
      },
      error: (err) => this.error.set(err?.message || 'Save failed.')
    });
  }

  toggleActive(a: AccountRow): void {
    const path = a.isActive ? `v1/accounts/${a.id}/deactivate` : `v1/accounts/${a.id}/activate`;
    this.api.post(path, {}).subscribe({
      next: () => this.load(),
      error: (err) => this.error.set(err?.message || 'Failed to update status.')
    });
  }
}
